import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Progress } from './components/ui/progress';
import { Input } from './components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Textarea } from './components/ui/textarea';

// Icons
import { 
  CheckCircle2, 
  Circle,
  Flame, 
  Target,
  Calendar,
  Clock,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Dumbbell,
  TrendingUp,
  Award,
  Sparkles,
  History,
  PartyPopper,
  RefreshCw
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// ==================== SHARED PROGRESS CONTEXT ====================
// This context links all progress components together

const ProgressContext = createContext(null);

export const ProgressProvider = ({ children }) => {
  const [workoutProgress, setWorkoutProgress] = useState(null);
  const [gamificationProgress, setGamificationProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const refreshAll = useCallback(async () => {
    const sessionToken = Cookies.get('session_token');
    if (!sessionToken) {
      setLoading(false);
      return;
    }

    try {
      // Fetch both workout and gamification progress in parallel
      const [workoutRes, gamificationRes] = await Promise.all([
        axios.get(`${API}/workout-progress`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }).catch(() => null),
        axios.get(`${API}/user/progress`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }).catch(() => null)
      ]);

      if (workoutRes) setWorkoutProgress(workoutRes.data);
      if (gamificationRes) setGamificationProgress(gamificationRes.data);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return (
    <ProgressContext.Provider value={{
      workoutProgress,
      gamificationProgress,
      loading,
      refreshAll,
      lastRefresh
    }}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    // Return a fallback for components not wrapped in ProgressProvider
    return {
      workoutProgress: null,
      gamificationProgress: null,
      loading: true,
      refreshAll: () => {},
      lastRefresh: Date.now()
    };
  }
  return context;
};

// ==================== GOAL ACHIEVED CELEBRATION ====================

const GoalAchievedCelebration = ({ onContinue, onStartNewWeek }) => {
  return (
    <div className="text-center py-6 space-y-4">
      <div className="relative">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
          <PartyPopper className="h-10 w-10 text-white" />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
          <span className="text-lg">🎉</span>
        </div>
      </div>
      
      <div>
        <h3 className="text-xl font-bold text-gray-900">Weekly Goal Achieved!</h3>
        <p className="text-gray-600 mt-1">You crushed it! Take a moment to celebrate.</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <p className="text-green-800 font-medium">
          💪 You&apos;re building amazing habits!
        </p>
        <p className="text-green-600 text-sm mt-1">
          Consistency is the key to results.
        </p>
      </div>

      <div className="flex gap-2 pt-2">
        <Button 
          variant="outline" 
          onClick={onContinue}
          className="flex-1"
        >
          Keep Going
        </Button>
        <Button 
          onClick={onStartNewWeek}
          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          New Week Goal
        </Button>
      </div>
    </div>
  );
};

// ==================== WORKOUT PROGRESS WIDGET ====================
// Main component for showing workout progress

export const WorkoutProgressWidget = ({ onCompleteWorkout, onViewProgress, onViewAchievements }) => {
  const [progress, setProgress] = useState(null);
  const [weeklyMessage, setWeeklyMessage] = useState('');
  const [streakMessage, setStreakMessage] = useState('');
  const [goalProgress, setGoalProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [goalAchieved, setGoalAchieved] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/workout-progress`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      setProgress(response.data.progress);
      setWeeklyMessage(response.data.weekly_message);
      setStreakMessage(response.data.streak_message);
      setGoalProgress(response.data.goal_progress_percent);
      
      // Check if goal was just achieved
      const { workouts_this_week, weekly_goal } = response.data.progress;
      const achieved = workouts_this_week >= weekly_goal;
      setGoalAchieved(achieved);
      
    } catch (error) {
      console.error('Error fetching workout progress:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Expose refresh function for external updates
  const refresh = useCallback(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    fetchProgress();
    
    // Set up event listener for progress updates
    const handleProgressUpdate = () => fetchProgress();
    window.addEventListener('workoutProgressUpdated', handleProgressUpdate);
    return () => window.removeEventListener('workoutProgressUpdated', handleProgressUpdate);
  }, [fetchProgress]);

  const handleStartNewWeek = async () => {
    // Increase weekly goal by 1 (optional challenge)
    try {
      const sessionToken = Cookies.get('session_token');
      const newGoal = Math.min(7, (progress?.weekly_goal || 3) + 1);
      await axios.put(`${API}/workout-progress/weekly-goal`, {
        weekly_goal: newGoal
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success(`Challenge accepted! New goal: ${newGoal} workouts/week 💪`);
      setShowCelebration(false);
      fetchProgress();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="py-6">
          <div className="h-24 bg-gray-100 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!progress) {
    return null;
  }

  const { total_workouts, workouts_this_week, weekly_goal, current_streak } = progress;

  // Show celebration modal
  if (showCelebration) {
    return (
      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardContent className="py-4">
          <GoalAchievedCelebration
            onContinue={() => setShowCelebration(false)}
            onStartNewWeek={handleStartNewWeek}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 transition-colors ${
      goalAchieved 
        ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50' 
        : 'border-blue-100 bg-gradient-to-br from-white to-blue-50'
    }`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            {goalAchieved ? (
              <Award className="h-5 w-5 text-green-600" />
            ) : (
              <Dumbbell className="h-5 w-5 text-blue-600" />
            )}
            <span>Your Progress</span>
          </div>
          {current_streak > 0 && (
            <div className="flex items-center gap-1 text-orange-500 text-sm font-normal">
              <Flame className="h-4 w-4" />
              <span>{current_streak} day streak</span>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Goal Achieved Banner */}
        {goalAchieved && (
          <button 
            onClick={() => setShowCelebration(true)}
            className="w-full p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg text-white text-center hover:from-green-600 hover:to-emerald-600 transition-colors"
          >
            <div className="flex items-center justify-center gap-2">
              <PartyPopper className="h-5 w-5" />
              <span className="font-medium">🎉 Weekly Goal Achieved! Tap to celebrate</span>
            </div>
          </button>
        )}

        {/* Weekly Message - Main motivation */}
        {!goalAchieved && (
          <div className="text-center py-2">
            <p className="text-lg font-medium text-gray-800">{weeklyMessage}</p>
            {streakMessage && (
              <p className="text-sm text-orange-600 mt-1">{streakMessage}</p>
            )}
          </div>
        )}

        {/* Weekly Goal Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Weekly Goal</span>
            <span className={`font-medium ${goalAchieved ? 'text-green-600' : ''}`}>
              {goalAchieved ? '✓ ' : ''}{workouts_this_week}/{weekly_goal} workouts
            </span>
          </div>
          <Progress 
            value={goalAchieved ? 100 : goalProgress} 
            className={`h-3 ${goalAchieved ? '[&>div]:bg-green-500' : ''}`}
          />
          {goalAchieved && (
            <p className="text-sm text-green-600 text-center font-medium">
              Goal complete! Keep the momentum going 🔥
            </p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="text-center p-2 bg-white rounded-lg border border-gray-100">
            <p className="text-2xl font-bold text-blue-600">{total_workouts}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className={`text-center p-2 rounded-lg border ${
            goalAchieved 
              ? 'bg-green-50 border-green-200' 
              : 'bg-white border-gray-100'
          }`}>
            <p className={`text-2xl font-bold ${goalAchieved ? 'text-green-600' : 'text-green-600'}`}>
              {workouts_this_week}
            </p>
            <p className="text-xs text-gray-500">This Week</p>
          </div>
          <div className="text-center p-2 bg-white rounded-lg border border-gray-100">
            <p className="text-2xl font-bold text-orange-500">{current_streak}</p>
            <p className="text-xs text-gray-500">Streak</p>
          </div>
        </div>

        {/* Complete Workout Button */}
        {onCompleteWorkout && (
          <Button 
            onClick={onCompleteWorkout}
            className={`w-full ${
              goalAchieved 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
            }`}
            data-testid="complete-workout-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {goalAchieved ? 'Log Another Workout' : 'Log a Workout'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== COMPLETE WORKOUT MODAL ====================

export const CompleteWorkoutModal = ({ open, onClose, onComplete, workoutPlan = null }) => {
  const [workoutName, setWorkoutName] = useState(workoutPlan?.title || '');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [completionData, setCompletionData] = useState(null);

  useEffect(() => {
    if (workoutPlan) {
      setWorkoutName(workoutPlan.title || '');
    }
  }, [workoutPlan]);

  useEffect(() => {
    // Reset state when modal opens
    if (open) {
      setJustCompleted(false);
      setCompletionData(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!workoutName.trim()) {
      toast.error('Please enter a workout name');
      return;
    }

    setSubmitting(true);
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.post(`${API}/workout-progress/complete`, {
        workout_name: workoutName,
        workout_plan_id: workoutPlan?.id,
        duration_minutes: duration ? parseInt(duration) : null,
        notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Show completion celebration
      setCompletionData(response.data);
      setJustCompleted(true);
      
      // Dispatch event to refresh all progress widgets
      window.dispatchEvent(new CustomEvent('workoutProgressUpdated'));
      
      // Also update gamification if we sync them
      try {
        await axios.post(`${API}/gamification/sync-workout`, {
          workout_name: workoutName
        }, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      } catch (e) {
        // Silently fail if gamification sync doesn't exist
      }
      
    } catch (error) {
      console.error('Error completing workout:', error);
      toast.error('Failed to log workout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setWorkoutName('');
    setDuration('');
    setNotes('');
    setJustCompleted(false);
    setCompletionData(null);
    
    if (onComplete && completionData) {
      onComplete(completionData);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        {justCompleted ? (
          // Celebration View
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {completionData?.message || 'Workout Complete!'}
              </h3>
              <p className="text-gray-600 mt-1">{completionData?.weekly_message}</p>
            </div>

            {/* Progress Update */}
            {completionData?.progress && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {completionData.progress.total_workouts}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {completionData.progress.workouts_this_week}
                    </p>
                    <p className="text-xs text-gray-500">This Week</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-500">
                      {completionData.progress.current_streak}
                    </p>
                    <p className="text-xs text-gray-500">Streak</p>
                  </div>
                </div>

                {/* Weekly Goal Progress */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Weekly Goal</span>
                    <span className="font-medium">
                      {completionData.progress.workouts_this_week}/{completionData.progress.weekly_goal}
                    </span>
                  </div>
                  <Progress 
                    value={Math.min(100, (completionData.progress.workouts_this_week / completionData.progress.weekly_goal) * 100)} 
                    className={`h-2 ${
                      completionData.progress.workouts_this_week >= completionData.progress.weekly_goal 
                        ? '[&>div]:bg-green-500' 
                        : ''
                    }`}
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={handleClose}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Awesome!
            </Button>
          </div>
        ) : (
          // Input Form
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span>Log Workout</span>
              </DialogTitle>
              <DialogDescription>
                Great job showing up! Let&apos;s record this workout.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Workout Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Workout Name</label>
                <Input
                  value={workoutName}
                  onChange={(e) => setWorkoutName(e.target.value)}
                  placeholder="e.g., Morning Run, Upper Body, Yoga"
                  data-testid="workout-name-input"
                />
              </div>

              {/* Duration (optional) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Duration (optional)
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    placeholder="Minutes"
                    className="pl-10"
                    data-testid="workout-duration-input"
                  />
                </div>
              </div>

              {/* Notes (optional) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Notes (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="How did it feel? Any achievements?"
                  rows={2}
                  data-testid="workout-notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={handleClose} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !workoutName.trim()}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  data-testid="submit-workout-btn"
                >
                  {submitting ? (
                    'Saving...'
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete ✓
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== WEEKLY GOAL SETTER ====================

export const WeeklyGoalSetter = ({ currentGoal, onGoalChange }) => {
  const [goal, setGoal] = useState(currentGoal || 3);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.put(`${API}/workout-progress/weekly-goal`, {
        weekly_goal: goal
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      toast.success(`Weekly goal set to ${goal} workouts`);
      if (onGoalChange) onGoalChange(goal);
    } catch (error) {
      console.error('Error setting goal:', error);
      toast.error('Failed to update goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
      <Target className="h-5 w-5 text-blue-500" />
      <div className="flex-1">
        <p className="text-sm font-medium">Weekly Goal</p>
        <p className="text-xs text-gray-500">How many workouts per week?</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setGoal(Math.max(1, goal - 1))}
          disabled={goal <= 1}
        >
          -
        </Button>
        <span className="w-8 text-center font-bold">{goal}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setGoal(Math.min(7, goal + 1))}
          disabled={goal >= 7}
        >
          +
        </Button>
        {goal !== currentGoal && (
          <Button 
            size="sm" 
            onClick={handleSave} 
            disabled={saving}
            className="ml-2"
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
};

// ==================== PROGRAM PROGRESS CARD ====================

export const ProgramProgressCard = ({ program, userProgress, onAction }) => {
  const [progress, setProgress] = useState(userProgress);
  const [loading, setLoading] = useState(!userProgress);

  useEffect(() => {
    if (!userProgress && program?.id) {
      fetchProgress();
    }
  }, [program, userProgress]);

  const fetchProgress = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/programs/${program.id}/progress`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching program progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.put(`${API}/programs/${program.id}/pause`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Program paused. Take all the time you need! 🌿');
      fetchProgress();
    } catch (error) {
      toast.error('Failed to pause program');
    }
  };

  const handleResume = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.put(`${API}/programs/${program.id}/resume`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Welcome back! Let\'s continue 💪');
      fetchProgress();
    } catch (error) {
      toast.error('Failed to resume program');
    }
  };

  const handleRestart = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.post(`${API}/programs/${program.id}/restart`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Fresh start! Let\'s do this 🌟');
      fetchProgress();
    } catch (error) {
      toast.error('Failed to restart program');
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="py-6">
          <div className="h-16 bg-gray-100 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  const completedWorkouts = progress?.progress?.completed_workouts || 0;
  const totalWorkouts = progress?.progress?.total_workouts_in_program || program?.workout_count || 0;
  const percentage = progress?.completion_percentage || 0;
  const status = progress?.progress?.status || 'active';
  const isComplete = percentage >= 100;

  return (
    <Card className={`border-2 ${isComplete ? 'border-green-200 bg-green-50' : status === 'paused' ? 'border-yellow-200 bg-yellow-50' : 'border-blue-100'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{program?.title || 'Program'}</CardTitle>
          {isComplete && (
            <div className="flex items-center gap-1 text-green-600">
              <Award className="h-5 w-5" />
              <span className="text-sm font-medium">Complete!</span>
            </div>
          )}
          {status === 'paused' && (
            <div className="flex items-center gap-1 text-yellow-600">
              <Pause className="h-4 w-4" />
              <span className="text-sm">Paused</span>
            </div>
          )}
        </div>
        {program?.description && (
          <CardDescription className="line-clamp-2">{program.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium">{completedWorkouts}/{totalWorkouts} workouts</span>
          </div>
          <Progress 
            value={percentage} 
            className={`h-3 ${isComplete ? '[&>div]:bg-green-500' : ''}`}
          />
          <p className="text-right text-sm text-gray-500">{percentage}% complete</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {status === 'paused' ? (
            <Button 
              onClick={handleResume}
              className="flex-1 bg-blue-500 hover:bg-blue-600"
            >
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          ) : !isComplete && (
            <Button 
              variant="outline"
              onClick={handlePause}
              className="flex-1"
            >
              <Pause className="h-4 w-4 mr-2" />
              Take a Break
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={handleRestart}
            className="flex-1"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Restart
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== WORKOUT HISTORY ====================

export const WorkoutHistory = ({ limit = 10 }) => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [limit]);

  const fetchHistory = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/workout-progress/history?limit=${limit}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setWorkouts(response.data.workouts);
    } catch (error) {
      console.error('Error fetching workout history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1,2,3].map(i => (
          <div key={i} className="h-16 bg-gray-100 rounded"></div>
        ))}
      </div>
    );
  }

  if (workouts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>No workouts logged yet</p>
        <p className="text-sm">Your workout history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {workouts.map((workout) => (
        <div 
          key={workout.id}
          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
        >
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-800">{workout.workout_name}</p>
            <p className="text-sm text-gray-500">
              {new Date(workout.completed_at).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
              {workout.duration_minutes && ` • ${workout.duration_minutes} min`}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== WORKOUT PROGRESS DASHBOARD ====================

export const WorkoutProgressDashboard = ({ open, onClose }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (open) {
      fetchProgress();
    }
  }, [open]);

  const fetchProgress = async () => {
    try {
      const sessionToken = Cookies.get('session_token');
      const response = await axios.get(`${API}/workout-progress`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('This will reset all your workout progress. Are you sure?')) {
      return;
    }
    
    try {
      const sessionToken = Cookies.get('session_token');
      await axios.post(`${API}/workout-progress/reset`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Progress reset! Ready for a fresh start 🌟');
      fetchProgress();
    } catch (error) {
      toast.error('Failed to reset progress');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span>Workout Progress</span>
          </DialogTitle>
          <DialogDescription>
            Track your fitness journey — one workout at a time
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Main Stats */}
            {progress && (
              <>
                {/* Weekly Message */}
                <div className="text-center py-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                  <Sparkles className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-lg font-medium text-gray-800">{progress.weekly_message}</p>
                  {progress.streak_message && (
                    <p className="text-sm text-orange-600 mt-1">{progress.streak_message}</p>
                  )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-3xl font-bold text-blue-600">
                      {progress.progress.total_workouts}
                    </p>
                    <p className="text-sm text-gray-500">Total Workouts</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-3xl font-bold text-green-600">
                      {progress.progress.workouts_this_week}
                    </p>
                    <p className="text-sm text-gray-500">This Week</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-xl">
                    <p className="text-3xl font-bold text-orange-500">
                      {progress.progress.current_streak}
                    </p>
                    <p className="text-sm text-gray-500">Current Streak</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <p className="text-3xl font-bold text-purple-600">
                      {progress.progress.longest_streak}
                    </p>
                    <p className="text-sm text-gray-500">Best Streak</p>
                  </div>
                </div>

                {/* Weekly Goal Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Weekly Goal Progress</span>
                    <span className="text-sm text-gray-500">
                      {progress.progress.workouts_this_week}/{progress.progress.weekly_goal} workouts
                    </span>
                  </div>
                  <Progress value={progress.goal_progress_percent} className="h-4" />
                  {progress.workouts_remaining > 0 ? (
                    <p className="text-sm text-gray-500 text-right">
                      {progress.workouts_remaining} more to reach your goal
                    </p>
                  ) : (
                    <p className="text-sm text-green-600 text-right font-medium">
                      🎉 Weekly goal achieved!
                    </p>
                  )}
                </div>

                {/* Weekly Goal Setter */}
                <WeeklyGoalSetter 
                  currentGoal={progress.progress.weekly_goal}
                  onGoalChange={() => fetchProgress()}
                />
              </>
            )}

            {/* Recent History */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <History className="h-5 w-5 text-gray-500" />
                <h3 className="font-medium">Recent Workouts</h3>
              </div>
              <WorkoutHistory limit={5} />
            </div>

            {/* Reset Option */}
            <div className="pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={handleReset}
                className="text-gray-500"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset All Progress
              </Button>
              <p className="text-xs text-gray-400 mt-1">
                Fresh start — no judgment, just a clean slate
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== SIMPLE WORKOUT CHECKLIST ====================
// For programs with multiple workouts

export const WorkoutChecklist = ({ workouts, completedIds, onComplete }) => {
  return (
    <div className="space-y-2">
      {workouts.map((workout, index) => {
        const isCompleted = completedIds?.includes(workout.id);
        
        return (
          <div 
            key={workout.id || index}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
              isCompleted 
                ? 'bg-green-50 border-green-200' 
                : 'bg-white border-gray-200 hover:border-blue-200'
            }`}
          >
            <button
              onClick={() => !isCompleted && onComplete(workout)}
              disabled={isCompleted}
              className="flex-shrink-0"
            >
              {isCompleted ? (
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              ) : (
                <Circle className="h-6 w-6 text-gray-300 hover:text-blue-500" />
              )}
            </button>
            <div className="flex-1">
              <p className={`font-medium ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                {workout.name || workout.title || `Workout ${index + 1}`}
              </p>
              {workout.duration && (
                <p className="text-sm text-gray-500">{workout.duration} min</p>
              )}
            </div>
            {!isCompleted && (
              <Button 
                size="sm"
                onClick={() => onComplete(workout)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Done
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WorkoutProgressWidget;
